/**
 * Adapters that normalize the various server response shapes used by the
 * Exercise / Exam / Trial Lab modules into the single `detailedGroups[]`
 * format consumed by <AnswerReviewDialog />.
 *
 * Normalized item shape:
 *   regular  : { type, partNumber, audioUrl, imageUrl, extraImageUrl, content,
 *               questions: [{ question, userAnswer, isCorrect, answered }] }
 *   dialogue : { type: "dialogue-group", partNumber, audioUrl, imageUrl,
 *               dialogueScript, blanks: [{position, correctAnswers}],
 *               dialogueQuestion: { question, userAnswer: {[pos]: string},
 *                                   isCorrect, answered },
 *               childQuestions: [{ question, userAnswer, isCorrect, answered }] }
 */

const makeResponseMap = (responses = [], idKey = "questionId") => {
  const map = {};
  responses.forEach((r) => {
    const key = r?.[idKey] ?? r?.question;
    if (key != null) map[key.toString()] = r;
  });
  return map;
};

const wrapQuestionResult = (question, response) => {
  if (!question) return null;
  if (!response) {
    return {
      question,
      userAnswer: null,
      isCorrect: false,
      answered: false,
    };
  }
  return {
    question,
    userAnswer: response.selectedOption ?? null,
    isCorrect: !!response.isCorrect,
    answered: true,
    timeSpent: response.timeSpent ?? 0,
  };
};

/**
 * Exercise sessions (LC / RC / Attendance Test) — input is the value returned
 * by `useGetExerciseSessionQuery(sessionId)`.
 *
 * Each `response` is `{ questionId, selectedOption, isCorrect, timeSpent }`.
 * `sectionData.parts[].questionGroups[]` carries the group / question objects.
 */
export function normalizeExerciseSession(sessionData) {
  if (!sessionData) return [];
  const parts = sessionData?.sectionData?.parts || [];
  const responseMap = makeResponseMap(sessionData?.responses || [], "questionId");
  const groups = [];

  parts.forEach((part) => {
    (part.questionGroups || []).forEach((group) => {
      if (group.type === "dialogue-group") {
        const parentQ = group.dialogueQuestion;
        const parentResp = parentQ ? responseMap[parentQ._id?.toString()] : null;
        const children = (group.childQuestions || []).map((c) =>
          wrapQuestionResult(c, responseMap[c._id?.toString()])
        );

        // For dialogue: parent "answered" if user filled at least one blank.
        const blanksAnswer = parentResp?.selectedOption || {};
        const parentAnswered =
          parentResp != null &&
          blanksAnswer &&
          typeof blanksAnswer === "object" &&
          Object.keys(blanksAnswer).length > 0;

        groups.push({
          type: "dialogue-group",
          partNumber: part.partNumber,
          audioUrl: group.audioUrl || parentQ?.audioUrl || null,
          imageUrl: group.imageUrl || parentQ?.imageUrl || null,
          extraImageUrl: group.extraImageUrl || null,
          dialogueScript: parentQ?.dialogueScript || group.dialogueScript || "",
          blanks: parentQ?.blanks || group.blanks || [],
          dialogueExplanation: parentQ?.dialogueExplanation || null,
          dialogueQuestion: parentQ
            ? {
                question: parentQ,
                userAnswer: blanksAnswer,
                isCorrect: !!parentResp?.isCorrect,
                answered: parentAnswered,
              }
            : null,
          childQuestions: children,
        });
      } else {
        const questions = (group.questions || []).map((q) =>
          wrapQuestionResult(q, responseMap[q._id?.toString()])
        );
        groups.push({
          type: group.type || "regular",
          partNumber: part.partNumber,
          audioUrl: group.audioUrl || null,
          imageUrl: group.imageUrl || null,
          extraImageUrl: group.extraImageUrl || null,
          content: group.content || null,
          questions,
        });
      }
    });
  });

  return groups;
}

/**
 * Exam attempts (TOEIC mock tests). The input is the rich `examAttempt`
 * object loaded by `getExamAnalytics`, with `exam.parts[].questionGroups[]`
 * and `responses[].question` as the question id reference.
 */
export function normalizeExamAttempt(examAttempt) {
  if (!examAttempt?.exam) return [];
  const responseMap = makeResponseMap(examAttempt.responses || [], "question");
  const groups = [];

  (examAttempt.exam.parts || []).forEach((part) => {
    (part.questionGroups || []).forEach((group) => {
      if (group.type === "dialogue-group") {
        const parentQ = group.dialogueQuestion;
        const parentResp = parentQ ? responseMap[parentQ._id?.toString()] : null;
        const children = (group.childQuestions || []).map((c) =>
          wrapQuestionResult(c, responseMap[c._id?.toString()])
        );

        const blanksAnswer = parentResp?.selectedOption || {};
        const parentAnswered =
          parentResp != null &&
          blanksAnswer &&
          typeof blanksAnswer === "object" &&
          Object.keys(blanksAnswer).length > 0;

        groups.push({
          type: "dialogue-group",
          partNumber: part.partNumber,
          audioUrl: group.audioUrl || parentQ?.audioUrl || null,
          imageUrl: group.imageUrl || parentQ?.imageUrl || null,
          extraImageUrl: group.extraImageUrl || null,
          dialogueScript: parentQ?.dialogueScript || group.dialogueScript || "",
          blanks: parentQ?.blanks || group.blanks || [],
          dialogueExplanation: parentQ?.dialogueExplanation || null,
          dialogueQuestion: parentQ
            ? {
                question: parentQ,
                userAnswer: blanksAnswer,
                isCorrect: !!parentResp?.isCorrect,
                answered: parentAnswered,
              }
            : null,
          childQuestions: children,
        });
      } else {
        const questions = (group.questions || []).map((q) => {
          const resp = responseMap[q._id?.toString()];
          // Exam responses may not carry `isCorrect`. Compute it from
          // `choices[].isCorrect` for MC questions when missing.
          if (resp && resp.isCorrect === undefined) {
            const correctChoice = q.choices?.find((c) => c.isCorrect);
            resp.isCorrect =
              correctChoice != null &&
              (resp.selectedOption === correctChoice.value ||
                resp.selectedOption === correctChoice._id?.toString());
          }
          return wrapQuestionResult(q, resp);
        });
        groups.push({
          type: group.type || "regular",
          partNumber: part.partNumber,
          audioUrl: group.audioUrl || null,
          imageUrl: group.imageUrl || null,
          extraImageUrl: group.extraImageUrl || null,
          content: group.content || null,
          questions,
        });
      }
    });
  });

  return groups;
}

/**
 * Public mock test attempt detail — input is the value returned by
 * `getPublicMockTestAttemptDetail` (`GET /exams/public/history/:attemptId`).
 *
 * Shape: `parts[].groups[].questions[]`, where each question already carries
 * `choices[].isCorrect`, the user's `selectedOption`, the `correctAnswer` and
 * a per-question `isCorrect` flag — so no response map / id matching is
 * needed; the detail is self-contained per question.
 */
export function normalizePublicMockTestAttempt(detail) {
  if (!detail?.parts) return [];
  const groups = [];

  (detail.parts || []).forEach((part) => {
    (part.groups || []).forEach((group) => {
      const questions = (group.questions || []).map((q) => ({
        question: q,
        userAnswer: q.selectedOption ?? null,
        isCorrect: !!q.isCorrect,
        answered: q.selectedOption != null && q.selectedOption !== "",
      }));
      groups.push({
        type: group.type || "regular",
        partNumber: part.partNumber,
        audioUrl: group.audioUrl || null,
        imageUrl: group.imageUrl || null,
        extraImageUrl: group.extraImageUrl || null,
        content: group.passage || group.content || null,
        questions,
      });
    });
  });

  return groups;
}

/**
 * Trial Lab — the BE already returns a normalized `detailedGroups[]`, so this
 * adapter is essentially a pass-through. Provided for symmetry.
 */
export function normalizeTrialLabResult(finalResult) {
  return finalResult?.detailedGroups || [];
}
